import { Heading } from '@mm/components';
import { useResolutionInfo } from '@mm/hooks';
import React, { useEffect, useState } from 'react';
import { ToggleSwitch } from '../../../../common/components/ToggleSwitch/ToggleSwitch';
import { MOBILE_SMALL } from '../../../../editor/constants/screenSize';
import { LeaderBadge } from '../../../../modules/LoginRouter/components/LoginLayout/components/Badges/LeaderBadge';
import metaLogoSrc from 'assets/meta-partner.png';
import styles from './NewBillingModal2023.module.css';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { getUniqId } from '@mm/utils';
import classNames from 'classnames';
import { PrimaryButton } from '../../../../common/buttons/PrimaryButton/PrimaryButton';
import InfographicDesktopSrc from '../AnalyzeWebsiteTraffic/infographic-desktop.png';
import InfographicMobileSrc from '../AnalyzeWebsiteTraffic/infographic-mobile.png';
import { useSelector } from 'react-redux';
import {
  activeFacebookPageIdSelector,
  selectActiveCompanyId,
} from '@mm/state/selectors/viewSelectors';
import { useSubscriptionCRUDActions } from '@mm/state/actions/SubscriptionCRUDActions';
import { StoreState } from '@mm/state/store-interfaces';
import {
  defaultProUnicornPlanSelector,
  defaultTeamPlanSelector,
  enhancedPlansSelector,
} from '@mm/state/selectors/subscriptionSelectors';
import { selectSessionSelector } from '@mm/state/selectors/userSelector';
import { useUsersCRUDActions } from '@mm/state/actions/UsersCRUDActions';
import { ContactSalesModal } from './ContactSalesModal/ContactSalesModal';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import {
  activeFacebookPageSelector,
  getPageWithBillingInfo,
} from '@mm/state/selectors/facebookPageSelector';
import { useConnectedPageCRUDActions } from '@mm/state/actions/ConnectedPageCRUDActions';
import { Logo } from '../../../../layout/BotBuilderSidebar/Logo/Logo';
import moneyBackSrc from 'assets/money-back-guarantee.png';
import { Overlay } from '@mm/components/src/Overlay/Overlay';
import { INewBillingModal2023Props } from '@mm/state/modules/modalsState/props';
import { notification } from 'antd';

export const NewBillingModal2023 = ({
  onClose,
  withCloseButton = true,
  hideFreePlan,
  hideUnicornPlan,
  className,
  withLogo = false,
  withOverlay = false,
  withDesiredAudienceInput = true,
  heading,
  subheading,
  css,
  onSuccessBilling,
}: INewBillingModal2023Props) => {
  const companyId = useSelector(selectActiveCompanyId);
  const activeFacebookPage = useSelector(activeFacebookPageSelector);
  const activeFacebookPageId = useSelector(activeFacebookPageIdSelector);
  const defaultUnicornPlan = useSelector(defaultProUnicornPlanSelector);
  const defaultTeamPlan = useSelector(defaultTeamPlanSelector);
  const { id: userId, userAttributes } = useSelector(selectSessionSelector);
  const plans = useSelector(enhancedPlansSelector);
  const plansMapping = useSelector(
    (state: StoreState) => state.subscription.plansMapping,
  );
  const basicFacebookPages = useSelector(
    (state: StoreState) => state.connectedPage.entities,
  );

  const [isAnnual, setIsAnnual] = useState(false);
  const [showContactSalesModal, setShowContactSalesModal] = useState(false);

  const { getPlans } = useSubscriptionCRUDActions();
  const { updateUserAttributes } = useUsersCRUDActions();
  const { retrieveBasicListByCompany, retrievePage } =
    useConnectedPageCRUDActions();
  const { openBillingModal } = useModalActions();
  const { screenWidth } = useResolutionInfo();
  const isMobile = screenWidth <= MOBILE_SMALL;

  const updateAttributes = () => {
    if (!userAttributes?.seenNewBilling2023Modal) {
      updateUserAttributes(companyId, userId, {
        seenNewBilling2023Modal: true,
      });
    }
  };

  const upgradeToPlan = async (isTeam: boolean) => {
    let enhancedBillingPage;
    if (!activeFacebookPage && !basicFacebookPages.length) {
      const res = await retrieveBasicListByCompany(companyId);
      enhancedBillingPage = getPageWithBillingInfo(
        res?.[0],
        plans,
        plansMapping,
      );
    } else {
      enhancedBillingPage = activeFacebookPage;
    }

    updateAttributes();

    const plan = isTeam ? defaultTeamPlan : defaultUnicornPlan;

    if (plan) {
      await openBillingModal({
        facebookPage: enhancedBillingPage,
        plan,
        manualRedirect: true,
        subscription: enhancedBillingPage?.subscription,
        upgradeFromSubscriptionId: enhancedBillingPage?.subscription
          ? enhancedBillingPage?.subscription.id
          : undefined,
        onSuccessBilling: async () => {
          onClose();
          await onSuccessBilling?.();

          if (activeFacebookPageId) {
            setTimeout(async () => {
              await retrievePage(activeFacebookPageId, false, false, true);
            }, 5000);
          }
        },
        onPlanCreatedFailed: () =>
          notification.error({
            message: 'Something went wrong',
            description:
              'Subscription is not created. Credit card is not charged.',
          }),
        isMonthly: !isAnnual,
      });
    }
  };

  const getPlanPrice = (isTeam: boolean) => {
    const planCode = isTeam
      ? plansMapping?.team[isAnnual ? 1 : 0]
      : plansMapping?.unicorn[isAnnual ? 1 : 0];

    const enhancedPlan = plans.find(p => p.planCode === planCode);
    return (enhancedPlan?.unitAmountInCents || 0) / 100;
  };

  const options = [
    {
      title: 'Free',
      price: '$0',
      pricePeriod: 'FREE',
      mainPoints: ['50  leads', '20 sends'],
      secondaryPoints: [
        'Outreach automation',
        'Automatically capture leads from anonymous traffic',
      ],
      backgroundColor: '#ECF0F6',
      buttonName: 'Get 50 Leads',
      onClick: () => {
        updateAttributes();
        onClose();
      },
      hide: hideFreePlan,
    },
    {
      title: 'Startup',
      price: `$${getPlanPrice(false)}`,
      pricePeriod: isAnnual ? '/year' : '/month',
      mainPoints: [
        'Up to 100 leads / month',
        'Best for brands with 500 visitors / month',
        '1,200 sends',
      ],
      secondaryPoints: [
        'Outreach automation',
        'Automatically capture leads from anonymous traffic',
        'Lead Generation from existing traffic',
        '100+ data points on every contact',
      ],
      backgroundColor: '#DEF0FA',
      buttonName: 'Get Startup',
      tag: ['Popular', '#08979C'],
      onClick: () => upgradeToPlan(false),
      hide: hideUnicornPlan,
    },
    {
      title: 'Team',
      price: `$${getPlanPrice(true)}`,
      pricePeriod: isAnnual ? '/year' : '/month',
      mainPoints: [
        '400 leads / month',
        'Best for brands with 2,000+ visitors / month',
        '3,000 sends',
      ],
      secondaryPoints: ['Everything in Startup', 'Dedicated support'],
      backgroundColor: '#D8E9FD',
      buttonName: 'Get Team',
      tag: ['Sale', '#FF7A45'],
      onClick: () => upgradeToPlan(true),
      hide: false,
    },
    {
      title: 'Enterprise',
      price: '',
      pricePeriod: '',
      description:
        'Best for companies with over 10,000 unique website visitors per month in order to capture over 2,000 unique leads from anonymous traffic.',
      backgroundColor: '#10239E',
      buttonName: 'Schedule or call me now',
      onClick: () => {
        updateAttributes();
        setShowContactSalesModal(true);
      },
      hide: false,
    },
  ];
  const filteredOptions = options.filter(option => !option.hide);

  useEffect(() => {
    if (!companyId) {
      return;
    }
    (async () => {
      if (!plansMapping) {
        await getPlans(companyId);
      }
    })();
  }, [plansMapping, companyId, getPlans]);

  return !showContactSalesModal ? (
    <div className={classNames(styles.container, className)}>
      {withCloseButton && (
        <CloseOutlined className={styles.closeIcon} onClick={onClose} />
      )}
      {withLogo && <Logo className={styles.logo} />}
      {!isMobile && (
        <div
          className={classNames(styles.badges, {
            [styles.badgesRight]: withLogo,
          })}
        >
          <LeaderBadge className={styles.leader} whiteStyle />
          <img src={metaLogoSrc} className={styles.meta} />
        </div>
      )}
      <div className={classNames(styles.header, css?.header)}>
        <Heading size={1} className={styles.heading}>
          {heading ? heading : 'One more step to lead generation'}
          <div className={styles.subheading}>
            {subheading ? subheading : 'Keep the leads coming!'}
          </div>
        </Heading>
        <ToggleSwitch
          isOn={isAnnual}
          handleToggle={() => setIsAnnual(!isAnnual)}
          leftSwitchText="Monthly"
          rightSwitchtext="Annual"
          classes={{
            container: styles.toggleContainer,
            trigger: styles.toggleTrigger,
            activeTrigger: styles.toggleTriggerActive,
          }}
        />
      </div>

      <div className={styles.content}>
        {filteredOptions.map(option => {
          return (
            <div
              key={option.title}
              className={styles.card}
              style={{ backgroundColor: option.backgroundColor }}
            >
              {option.tag && (
                <div
                  style={{ backgroundColor: option.tag[1] }}
                  className={styles.tag}
                >
                  {option.tag[0]}
                </div>
              )}
              <div className={styles.title}>
                <div
                  className={classNames(styles.heading, {
                    [styles.white]: option.title === 'Enterprise',
                  })}
                >
                  {option.title}
                </div>
                <div className={styles.price}>
                  {option.price}{' '}
                  <span className={styles.smallText}>{option.pricePeriod}</span>
                </div>
              </div>
              <div className={styles.description}>
                {option.description && (
                  <div className={classNames(styles.text, styles.white)}>
                    {option.description}
                  </div>
                )}
                <div className={styles.pointsContainer}>
                  {option.mainPoints?.map(point => {
                    return (
                      <div className={styles.point} key={getUniqId()}>
                        <CheckOutlined className={styles.checkmark} />
                        {point}
                      </div>
                    );
                  })}
                </div>

                <div className={styles.pointsContainer}>
                  {option.secondaryPoints?.map(point => {
                    return (
                      <div
                        className={classNames(
                          styles.point,
                          styles.secondaryPoint,
                        )}
                        key={getUniqId()}
                      >
                        <CheckOutlined className={styles.checkmark} />
                        {point}
                      </div>
                    );
                  })}
                </div>
              </div>
              <PrimaryButton
                className={classNames(styles.button, {
                  [styles.white]: option.title === 'Enterprise',
                })}
                onClick={option.onClick}
              >
                {option.buttonName}
              </PrimaryButton>
            </div>
          );
        })}
      </div>
      <div>
        {isMobile ? (
          <img src={InfographicMobileSrc} />
        ) : (
          <img src={InfographicDesktopSrc} />
        )}
      </div>
      {withLogo && <img src={moneyBackSrc} className={styles.moneyBackLogo} />}
    </div>
  ) : withOverlay ? (
    <Overlay>
      <ContactSalesModal
        onClose={() => setShowContactSalesModal(false)}
        onParentClose={onClose}
        withDesiredAudienceInput={withDesiredAudienceInput}
      />
    </Overlay>
  ) : (
    <ContactSalesModal
      onClose={() => setShowContactSalesModal(false)}
      onParentClose={onClose}
      withDesiredAudienceInput={withDesiredAudienceInput}
    />
  );
};
